import { LineChartAnomalyValue } from '@modules/charts/types';
import {
  DateTimeRange,
  TimePeriodUnit,
} from '@modules/common/components/DateRangePicker/types';
import {
  ReportDataPoint,
  SortBy as CommonSortBy,
  ZustandStoreWithDispatch,
} from '@modules/common/types';
import { QueryBuilderAction } from '@modules/queryBuilder/store/types';
import {
  EqlField,
  IntermediateCsatFuncValue,
  IntermediateMeanFuncValue,
  IntermediateNpsFuncValue,
  SentimentType,
} from 'generated/graphql';
import { AggregatePeriod } from 'v2/core/entities';
import { UUID } from 'v2/core/entities/uuid';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { NormalisedNode } from 'v2/querybuilder/types/querystore';
import { QuantifyActionType } from './quantifyActionType';

export type QuantifyReportDataPointBase = Pick<
  ReportDataPoint,
  | 'id'
  | 'label'
  | 'category'
  | 'isRelevant'
  | 'isInvisible'
  | 'isUpdateInProgress'
>;

export interface QuantifyReportDataPoint extends QuantifyReportDataPointBase {
  value: number;
  timestamp: number | undefined;
  entitySentiment: SentimentType | undefined;
  anomalyValue: LineChartAnomalyValue | undefined;
  isAnomalous: boolean;
  isRelevant: boolean;
  nps?: Omit<IntermediateNpsFuncValue, '__typename'>;
  csat?: Omit<IntermediateCsatFuncValue, '__typename'>;
  mean?: Omit<IntermediateMeanFuncValue, '__typename'>;
  variants?: Record<
    string,
    {
      id: string;
      label: string;
      value: number;
      anomalyValue?: LineChartAnomalyValue;
      nps?: Omit<IntermediateNpsFuncValue, '__typename'>;
      csat?: Omit<IntermediateCsatFuncValue, '__typename'>;
      mean?: Omit<IntermediateMeanFuncValue, '__typename'>;
    }
  >;
}

export interface CompareReportDataPoint extends QuantifyReportDataPointBase {
  prevValue: number;
  currValue: number;
  relativeDelta: number; // percentage
  absoluteDelta: number; // numeric delta: -2, 4
  absValueDelta: number; // abs value delta: 2, 4
  isRelevant: boolean;
  nps?: {
    prevValue?: Omit<IntermediateNpsFuncValue, '__typename'>;
    currValue?: Omit<IntermediateNpsFuncValue, '__typename'>;
  };
  csat?: {
    prevValue?: Omit<IntermediateCsatFuncValue, '__typename'>;
    currValue?: Omit<IntermediateCsatFuncValue, '__typename'>;
  };
  mean?: {
    prevValue?: Omit<IntermediateMeanFuncValue, '__typename'>;
    currValue?: Omit<IntermediateMeanFuncValue, '__typename'>;
  };
}

export type SentimentVisualisationType =
  | VisualisationType.TREND
  | VisualisationType.BAR;

export type SortBy = CommonSortBy;

export interface CompareToWhereClauseFilter {
  type: 'whereClause';
  whereClause: WhereClause | null;
  denominatorWhereClause: WhereClause | null;
}

export interface CompareToPreviousPeriod {
  type: 'offset';
  value: number;
  unit: TimePeriodUnit;
}
export interface CompareToCustomDuration {
  type: 'timerange';
  duration: DateTimeRange;
}

export interface QuantifyQuery {
  groupBy: EqlField | null; // For "Show me" field, "Group By" was the old name
  compareTo:
    | CompareToWhereClauseFilter
    | CompareToPreviousPeriod
    | CompareToCustomDuration
    | null;
  whereClauseConfig: {
    whereClause: WhereClause | null;
  } | null;
  duration: DateTimeRange;
  /**
   * @deprecated The property should not be used, use formula instead.
   */
  shouldShowNormalisedValues: boolean;
  formulaID:
    | EQLFunctionName.Count
    | EQLFunctionName.NormaliseByPercent
    | EQLFunctionName.NPS
    | EQLFunctionName.CSAT
    | EQLFunctionName.Mean
    | EQLFunctionName.Sum;
  measuredAsCriteria: EqlField | null;
  denominatorWhereClause: WhereClause | null;
}

export enum VisualisationType {
  BAR = 'BAR',
  TREND = 'TREND',
  COUNT = 'COUNT',
  PIE = 'PIE',
}

export interface CompareQBField {
  dispatch: (action: QueryBuilderAction) => QueryBuilderAction;
  rootID: string;
  nodesByID: Record<string, NormalisedNode>;
}

export interface QuantifyState {
  query: QuantifyQuery;
  metadata: {
    visibleDataPoints: UUID[];
    shouldShowAllValues: boolean;
    sortBy: SortBy | null;
    activeVisualisationType: VisualisationType;
    aggregatePeriod: AggregatePeriod;
    shouldShowCooccurringDataPoints: boolean;
    shouldShowTopDataPoints: boolean;
    topDataPointsSelectionCount: number;
    shouldShowAnomalies: boolean;
    anomalyThreshold: number;
    shouldShowCategories: boolean;
    compareQBField: {
      compareQBFieldAName?: string;
      compareQBFieldBName?: string;
    };
    compareDurationField: {
      compareDurationAName?: string;
      compareDurationBName?: string;
    };
  };
  visual: {
    hasQueryChanged: boolean;
    hasMetadataChanged: boolean;
    isEditMode: boolean;
    isTableQueryInProgress: boolean;
    isChartQueryInProgress: boolean;
    isCustomAnomalyThresholdSelected: boolean;
    pageNumber: number;
    timeRangeTraversal: {
      originalDuration: DateTimeRange;
      traverseCount: number;
    };
    searchTerm: string;
    totalAggregateHits: number;
    tableAggregateHits: number;
    shouldHighlightWhereClauseField: boolean;
    shouldHighlightDurationField: boolean;
    shouldSkipTrendChart?: boolean;
    shouldSkipPercentageFormula?: boolean;
  };
  reportData: {
    quantify: {
      aggregateFormulaResponse: QuantifyReportDataPoint | null;
      tableRows: QuantifyReportDataPoint[] | null;
      chartDataPoints: QuantifyReportDataPoint[] | null;
      valueRange: { start: number; end: number } | null;
    };
    compare: {
      tableRows: CompareReportDataPoint[] | null;
      chartDataPoints: CompareReportDataPoint[] | null;
      aggregateFormulaResponse: CompareReportDataPoint | null;
      valueRange: { start: number; end: number } | null;
    };
  };
  misc: {
    compareQBFieldA: CompareQBField | null;
    compareQBFieldB: CompareQBField | null;
  };
}

interface QuantifyQueryBuilderAction {
  type:
    | QuantifyActionType.INIT_QUERY
    | QuantifyActionType.SET_QUERY
    | QuantifyActionType.RESET_QUERY;
  payload?: Partial<QuantifyState['query']>;
}

interface QuantifyMetaDataAction {
  type: QuantifyActionType.INIT_METADATA | QuantifyActionType.SET_METADATA;
  payload: Partial<QuantifyState['metadata']>;
}

interface QuantifyVisualAction {
  type: QuantifyActionType.SET_VISUAL;
  payload: Partial<QuantifyState['visual']>;
}

interface QuantifyReportDataAction {
  type: QuantifyActionType.SET_REPORT_DATA;
  payload: Partial<{
    quantify: Partial<QuantifyState['reportData']['quantify']>;
    compare: Partial<QuantifyState['reportData']['compare']>;
  }>;
}

interface QuantifyInitAction {
  type: QuantifyActionType.INIT;
  payload: Partial<QuantifyState>;
}

interface QuantifyMiscAction {
  type: QuantifyActionType.SET_MISC;
  payload: Partial<QuantifyState['misc']>;
}

export type QuantifyAction =
  | QuantifyQueryBuilderAction
  | QuantifyMetaDataAction
  | QuantifyVisualAction
  | QuantifyReportDataAction
  | QuantifyInitAction
  | QuantifyMiscAction;

export type QuantifyStateWithRedux = ZustandStoreWithDispatch<
  QuantifyState,
  QuantifyAction
>;
